import { useQuery } from "@tanstack/react-query";
import type { ApiCidade } from "features/cidade-uf-pais/api";
import type { ApiColeta } from "pages/coleta/types/ApiColeta";
import type { ApiProdutor } from "pages/produtores/types/ApiProdutor";
import { apiRelColetasCidade } from "pages/relatorios/api/apiRelColetasCidade";
import { RelatorioContainer } from "pages/relatorios/components/RelatorioContainer";
import { useRelatorioStore } from "pages/relatorios/store";
import type { ApiRelatorioGetParams } from "pages/relatorios/types/ApiRelatorioParams";
import { buildRelatorioGetParams } from "pages/relatorios/utils/buildRelatorioGetParams";
import { useEffect, useMemo } from "react";
import type { TExportToCSV } from "std/csv";
import { FilterBar } from "./FilterBar";
import { columns, TableRelColetasCidade } from "./TableRelColetasCidade";

export type TApiColetasCidadeItem = {
	produtoNome: string;
	quantidade: number;
	produtor: ApiProdutor;
	coleta: ApiColeta;
};

export type TApiColetasCidade = ApiCidade & {
	coletas: TApiColetasCidadeItem[];
};

export function RelatorioColetasCidade() {
	const filter = useRelatorioStore((s) => s.filter);

	const limparFiltros = useRelatorioStore((s) => s.limparFiltros);

	const params: ApiRelatorioGetParams = useMemo(() => buildRelatorioGetParams(filter), [filter]);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ["apiRelColetasCidade", params],
		queryFn: () => apiRelColetasCidade({ params }),
		enabled: false,
	});

	const registros = useMemo(() => data?.registros || [], [data]);

	const csvData: TExportToCSV<TApiColetasCidade> = useMemo(() => {
		return {
			type: "multitable",
			fileName: "relatorio-coletas-cidade",
			options: {
				// biome-ignore lint/suspicious/noExplicitAny: dynamic
				columns: columns as any,
				data: registros,
				parentColumn: "cid_cidade",
				childKey: "coletas",
			},
		};
	}, [registros]);

	useEffect(() => {
		limparFiltros();
	}, [limparFiltros]);

	const title = "Dados de Cidades do produtor";

	return (
		<RelatorioContainer
			breadCrumbs={["Relatórios", "Cidades", "Produtor"]}
			refetch={refetch}
			filterBar={<FilterBar />}
			loading={isFetching}
			title={title}
			csvData={csvData}
			registros={registros}
			table={<TableRelColetasCidade data={data?.registros} title={title} />}
		/>
	);
}
